import { useEffect, useMemo, useRef, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import throttle from "lodash.throttle";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Alert,
  Text,
  View,
} from "react-native";
import { useEvent } from "@/features/events/api/events";
import {
  useEventParticipants,
  useJoinEvent,
  useLeaveEvent,
  type ParticipantType,
} from "@/features/events/api/event-participants";
import { useEventCoupon } from "@/features/events/api/event-coupon";
import { getCouponRevealState } from "@/features/events/lib/coupon";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { useProfile } from "@/features/profile/api/profile";
import {
  EVENT_PARTICIPANT_CONFIRMED,
  EVENT_PARTICIPANT_INTERESTED,
} from "@/features/events/constants";
import { EventDetailView } from "@/features/events/components/event-detail-view";
import { AddToCalendarSheet } from "@/features/events/components/add-to-calendar-sheet";
import { posthog } from "@/shared/lib/posthog";

/**
 * Event detail for logged-in users: full read + join/leave behavior.
 * Rendered by `event-detail.tsx` only when `isLoggedIn` is true.
 */
export default function AuthedEventDetail() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const { session } = useAuthContext();
  const router = useRouter();
  const userId = session?.user.id;
  const { data: profile } = useProfile(userId);
  const [showCalendarSheet, setShowCalendarSheet] = useState(false);

  const { data: event, isLoading, error } = useEvent(eventId);
  const { data: participants, isLoading: isLoadingParticipants } =
    useEventParticipants(eventId);

  const userParticipant = useMemo(
    () => participants?.find((p) => p.userId === userId),
    [participants, userId]
  );
  const isUserConfirmed =
    userParticipant?.participantType === EVENT_PARTICIPANT_CONFIRMED;
  const isUserInterested =
    userParticipant?.participantType === EVENT_PARTICIPANT_INTERESTED;

  const {
    data: coupon,
    isLoading: isCouponLoading,
    isError: isCouponError,
  } = useEventCoupon(
    eventId,
    userId,
    Boolean(isUserConfirmed && event?.hasCoupon)
  );

  const couponReveal = getCouponRevealState(
    isCouponLoading,
    isCouponError,
    coupon
  );

  const joinEvent = useJoinEvent();
  const leaveEvent = useLeaveEvent();

  const joinEventRef = useRef(joinEvent);
  joinEventRef.current = joinEvent;

  const leaveEventRef = useRef(leaveEvent);
  leaveEventRef.current = leaveEvent;

  const joinEventThrottled = useMemo(
    () =>
      throttle(
        (participantType: ParticipantType) => {
          joinEventRef.current.mutate(
            {
              eventId,
              userId: userId!,
              participantType,
              userProfile: profile!,
            },
            {
              onSuccess: () => {
                AccessibilityInfo.announceForAccessibility(
                  "Successfully joined the event"
                );
                if (participantType === EVENT_PARTICIPANT_CONFIRMED) {
                  posthog?.capture("event_attendance_confirmed", {
                    event_id: eventId,
                    has_coupon: event?.hasCoupon ?? false,
                  });
                } else {
                  posthog?.capture("event_interest_marked", {
                    event_id: eventId,
                  });
                }
              },
              onError: (err) => {
                const isAuthError =
                  err.message?.includes("logged in") ||
                  err.message?.includes("auth");
                Alert.alert(
                  "Join Failed",
                  isAuthError
                    ? "You must be logged in to join events."
                    : "Failed to join event. Please try again."
                );
              },
            }
          );
        },
        2000,
        { leading: true, trailing: false }
      ),
    [eventId, userId, profile]
  );

  const leaveEventThrottled = useMemo(
    () =>
      throttle(
        () => {
          leaveEventRef.current.mutate(
            {
              eventId,
              userId: userId!,
            },
            {
              onSuccess: () => {
                AccessibilityInfo.announceForAccessibility(
                  "Successfully left the event"
                );
                posthog?.capture("event_left", { event_id: eventId });
              },
              onError: () => {
                Alert.alert(
                  "Leave Failed",
                  "Failed to leave event. Please try again."
                );
              },
            }
          );
        },
        2000,
        { leading: true, trailing: false }
      ),
    [eventId, userId]
  );

  useEffect(() => {
    return () => {
      joinEventThrottled.cancel();
      leaveEventThrottled.cancel();
    };
  }, [joinEventThrottled, leaveEventThrottled]);

  if (isLoading) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <ActivityIndicator
          size="large"
          className="text-brand"
          accessibilityLabel="Loading event details"
        />
      </View>
    );
  }

  if (error || !event) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <Text
          className="text-destructive text-center text-base"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          Failed to load event
        </Text>
      </View>
    );
  }

  return (
    <>
      <EventDetailView
        event={event}
        participants={participants}
        isLoadingParticipants={isLoadingParticipants}
        isUserConfirmed={isUserConfirmed}
        isUserInterested={isUserInterested}
        isInterestPending={joinEvent.isPending || leaveEvent.isPending}
        isConfirmPending={
          isUserConfirmed ? leaveEvent.isPending : joinEvent.isPending
        }
        onToggleInterest={() => {
          if (isUserConfirmed) return;
          if (isUserInterested) {
            leaveEventThrottled();
          } else {
            joinEventThrottled(EVENT_PARTICIPANT_INTERESTED);
          }
        }}
        onPressConfirm={() =>
          isUserConfirmed
            ? leaveEventThrottled()
            : joinEventThrottled(EVENT_PARTICIPANT_CONFIRMED)
        }
        onPressParticipants={() =>
          router.push(`/event/${eventId}/participants`)
        }
        onPressAddToCalendar={() => setShowCalendarSheet(true)}
        couponReveal={couponReveal}
      />
      <AddToCalendarSheet
        visible={showCalendarSheet}
        onClose={() => setShowCalendarSheet(false)}
        event={event}
      />
    </>
  );
}
